'use client';

import { useConfig } from '@/hooks/useConfig';
import { raise } from '@/lib/utils';
import type { BaseInstance } from '@pigello/pigello-matrix';
import { isArray } from 'es-toolkit/compat';
import { resolveCurrentUserInFilterIds } from '@/requests/hooks/filter-rewrites';
import type { AppliedFilter, Filter } from './constants';
import { getInstanceDisplayValue } from '@/app/detail/inspectionerrand/[id]/content/utils';
import { useFetchAllIds } from '@/requests/hooks/use-fetch-all-ids';

export default function RelationDisplay<T extends BaseInstance>({
  filterConfig,
  filter,
}: {
  filterConfig: Filter;
  filter: AppliedFilter;
}) {
  if (!filterConfig.relationModelName)
    raise('Modelname missing from filter config');

  if (!isArray(filter.value))
    raise(`Can't pass ${typeof filter.value} to relation display.`);

  const resolvedIds = resolveCurrentUserInFilterIds(filter.value);

  const { data: values } = useFetchAllIds<T>({
    modelName: filterConfig.relationModelName,
    ids: resolvedIds,
    idAttr: 'id',
    enabled: resolvedIds.length > 0,
  });

  const { config } = useConfig<T>(filterConfig.relationModelName);

  if (!config || !values?.list) return 'Laddar...';

  const firstItem = values.list.at(0);

  const displayValue = getInstanceDisplayValue(
    firstItem,
    filterConfig.relationModelName
  );

  const count = values.list.filter((val) =>
    resolvedIds.includes(val.id)
  ).length;
  return (
    <div className='flex items-center'>
      {count === 1 && String(displayValue ?? 'Okänt objekt')}{' '}
      {count > 1 && `${count} ${config.verboseNamePlural.toLowerCase()}`}
      {count === 0 && '...'}
    </div>
  );
}
